import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import AuthShell from "../../Common/AuthShell/AuthShell";
import BoxForm from "../../Common/BoxForm/BoxForm";
import Button from "../../Common/Button";
import PasswordInput from "../../Common/Input/PasswordInput";
import { useTitle } from "../../Utilities/TitleHook";

const ResetPassword: FC = () => {
    useTitle("Reset Password");

    const location = useLocation();
    const [ email, setEmail ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ passwordConfirmation, setPasswordConfirmation ] = useState("");
    const [ token, setToken ] = useState("");
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<null | string>(null);
    const [ success, setSuccess ] = useState<null | true>(null);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        setEmail(searchParams.get("email") ?? "");
        setToken(searchParams.get("token") ?? "");
    }, [ location ]);

    const onSubmit = () => {
        if(loading) {
            return;
        }

        if(!email || !token) {
            setError("An unknown error has occurred, please try refreshing the page");

            return;
        }

        if(!password) {
            setError("Please enter a valid password");

            return;
        }

        if(!passwordConfirmation) {
            setError("Please confirm your new password");

            return;
        }

        if(password !== passwordConfirmation) {
            setError("Passwords do not match");

            return;
        }

        setLoading(true);

        fetch("/auth/reset", {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                token,
                email,
                password,
                password_confirmation: passwordConfirmation,
            }),
        })
            .then(response => {
                setLoading(false);

                if(response.status !== 204) {
                    throw new Error("We were unable to reset your password at this time");
                }
            })
            .then(() => {
                setSuccess(true);
            })
            .catch((err) => {
                console.error(err);

                if(err instanceof Error) {
                    setError(err.message);
                } else {
                    setError(err.toString());
                }
            });
    };

    if(success) {
        return (
            <AuthShell>
                <BoxForm
                    error={error}
                    onClear={() => {
                        setError(null);
                    }}
                >
                    <p>Your password was successfully reset</p>
                    <p>
                        <Link to="/login">
                            <span className="link-primary">Click here to log in</span>
                        </Link>
                    </p>
                </BoxForm>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <BoxForm
                error={error}
                onClear={() => {
                    setError(null);
                }}
            >
                <h2 className="mt-2">Reset Your Password</h2>
                <PasswordInput
                    icon="key"
                    label="Password"
                    onChange={e => {
                        setError(null);
                        setPassword(e.currentTarget.value);
                    }}
                    value={password}
                    onKeyDown={e => {
                        if(e.key === "Enter") {
                            onSubmit();
                        }
                    }}
                />
                <PasswordInput
                    icon="key"
                    label="Confirm Password"
                    onChange={e => {
                        setError(null);
                        setPasswordConfirmation(e.currentTarget.value);
                    }}
                    value={passwordConfirmation}
                    onKeyDown={e => {
                        if(e.key === "Enter") {
                            onSubmit();
                        }
                    }}
                />

                <Button onClick={onSubmit}>Change Password</Button>
            </BoxForm>
        </AuthShell>
    );
};

export default ResetPassword;
